import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import GeneralGoodsItem from "../general-goods-item";
import HomeTabsBar from "../home-tabs-bar";
import AgreementRow from "../agreement-row";
import { useGoodsFocus } from "./GoodsFocusContext";
import BuyGoodContent from "../buy-good-content";
import { Image } from "@xp-design/mobile";
import "./index.less";
import { PostPreBuyMemberGoods } from "../../api";
import { goToPageV2 } from "@/utils/common";
import { DEFAULT_ERROR_MSG } from "../../config";
import { P_MALL_APPID } from "@/utils/constants";
import {
  entry,
  getReportCompData,
  getReportGoodsData,
  getReportVipData,
  showToast,
} from "../../utils";
import { trackGdp } from "@/utils/gio-report";
import { useUserContext } from "../../routes/home/userContext";
import { TabItem, GoodsItem, GoodsGroup } from "../../types";

import { BaseModuleJson } from "@/types/config-page";
import { processOssImage } from "@/utils/image";
import { getParamsFromHash } from "@/utils/url";
import { useConfigPage } from "@/hooks/useConfigPage/useConfigPage";
import loading from "@xp-design/mobile/lib/loading";
import PopupCom, { PopupComRef } from "@/component/popupCom";
import { useSpring, animated, config } from "@react-spring/web";
import useMeasure from "react-use-measure";
import * as Sentry from "@sentry/react";

const TabGoodsContent = ({
  comp,
  moduleJsonData,
  group,
  type,
  isInPopup = false,
  isShowButtomBuyContent, // 新增：标识是否是第一个TabGoodsContent
  onStickyBuyContentVisible, // 新增：回调函数，通知父组件底部BuyGoodContent的显示状态
}: {
  comp: BaseModuleJson;
  moduleJsonData: BaseModuleJson;
  group: GoodsGroup[];
  type?:
    | "ONE_ROW_GOODS_BUY"
    | "ONE_ROW_X_GOODS"
    | "ONE_ROW_ONE_GOODS_BUY"
    | "ONE_ROW_X_GOODS_BUY";
  isInPopup?: boolean;
  isShowButtomBuyContent?: boolean;
  onStickyBuyContentVisible?: (visible: boolean) => void;
}) => {
  const {
    focusedGoods,
    setFocusedGoods,
    setBuyAgreement,
    setDetailRule,
    currentGroups,
    setCurrentGroups,
    setCredit,
    countNum,
    setCountNum,
    currentGoodsList,
    setCurrentGoodsList,
  } = useGoodsFocus();
  const { configPageQuery } = useConfigPage();
  const [memberBuyCurrentTab, setMemberBuyCurrentTab] = useState<number | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const [activeKey, setActiveKey] = useState<number | null>(null);

  const memberCardTabItems = useMemo(() => {
    return (
      currentGroups?.map((item) => ({
        type: item.id,
        key: item.id,
        title: item.name,
        subTitle: item.subTitle,
      })) || []
    );
  }, [currentGroups]);

  const spuId = getParamsFromHash("spuId") || "";

  // 活动透传spuId对应的商品信息
  const marketingGoodItem = useMemo(() => {
    const goods =
      currentGroups?.find((item) =>
        item.goodsList?.some((goodsItem) => goodsItem.spuId === spuId),
      )?.goodsList || [];
    console.log(
      "goods",
      goods,
      currentGroups?.find((item) =>
        item.goodsList?.some((goodsItem) => goodsItem.spuId === spuId),
      ),
    );
    return goods?.find((item) => item.spuId === spuId);
  }, [currentGroups, spuId]);

  const { levelInfo, balanceCredit, goodsData, userType } = useUserContext();

  // 获取视觉样式配置
  const visualStyle = moduleJsonData?.visualStyle;
  const bgUrl = visualStyle?.bgUrl;
  const spacing = visualStyle?.spacing || 0;
  const GoodsPopupRef = useRef<PopupComRef>(null);
  const HomeTabsBarRef = useRef<{
    clickTab: (tabItem: TabItem) => void;
    setHlTab: (tabKey: number | null) => void;
  }>(null);

  // 新增状态：跟踪原BuyGoodContent是否可见
  const [isOriginalBuyContentVisible, setIsOriginalBuyContentVisible] =
    useState(false);

  // 当isOriginalBuyContentVisible状态改变时，通知父组件
  useEffect(() => {
    if (isShowButtomBuyContent && onStickyBuyContentVisible) {
      // 当isShowButtomBuyContent为true且原BuyGoodContent不可见时，底部的sticky BuyGoodContent会显示
      onStickyBuyContentVisible(!isOriginalBuyContentVisible);
    }
  }, [
    isOriginalBuyContentVisible,
    isShowButtomBuyContent,
    onStickyBuyContentVisible,
  ]);
  const buyContentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setCredit(balanceCredit);
  }, [balanceCredit, setCredit]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCountNum(countNum ? countNum + 1 : 1);
    }, 1000);
    return () => clearTimeout(timeoutId); // 清理定时器
  }, [countNum, setCountNum]);

  // 创建防抖版本的购买函数，设置300ms的防抖延迟
  const debouncedBuyMemberGoods = useCallback(
    async (good: GoodsItem, withOutActCode = false) => {
      loading.show("加载中...");
      const preTradeOrderType =
        good?.discountType === "SECONDS_KILL_PRICE" ? 6 : undefined;
      trackGdp("P11222", "B005", {
        activityld: currentGoodsList
          ?.map((item) => item.actCode)
          ?.filter(Boolean)
          ?.join(","),
        pageId: configPageQuery?.data?.pageCode,
        versionId: configPageQuery?.data?.versionCode,
        userType,
        ...getReportCompData(comp),
        ...getReportVipData(levelInfo),
        ...getReportGoodsData(good),
      });
      try {
        const params = {
          actCode: withOutActCode ? "" : good.actCode,
          sku: good.skuId,
          spu: good.spuId,
          preTradeOrderType,
        };
        const res: {
          code: number;
          data: {
            orderUrl: string;
          };
          msg: string;
        } = await PostPreBuyMemberGoods(params);
        if (res.code === 200) {
          if (res.data?.orderUrl) {
            goToPageV2({
              url: encodeURI(
                `${res.data?.orderUrl}?entry=${entry()}&advanceOrderType=${preTradeOrderType}`,
              ),
              appId: P_MALL_APPID,
            });
            loading.hide();
          }
        } else {
          loading.hide();
          const errerCodeMsg = {
            65012001: "活动火爆，请稍后再试",
            65012002: "活动火爆，请稍后再试",
            65012003: "您已达到购买上限",
            65012004: "秒杀活动尚未开始",
            65012005: "抱歉，活动已结束",
            4002001: "活动火爆，当前价格已失效，请刷新",
            429: "触发接口限流",
          };
          showToast(errerCodeMsg[res?.code] || res?.msg);
        }
      } catch (error) {
        console.log("doBuyMemberGood -->", error);
        Sentry.captureException(error, {
          tags: {
            error_type: "doBuyMemberGoods_exception",
            biz: "charge-vip",
          },
        });
        showToast(DEFAULT_ERROR_MSG);
      } finally {
        loading.hide();
      }
    },
    [currentGoodsList, moduleJsonData, levelInfo],
  );

  // 保持原有方法名不变，实际调用防抖版本
  const doBuyMemberGoods = (good: GoodsItem, withOutActCode = false) => {
    debouncedBuyMemberGoods(good, withOutActCode);
  };

  const tabsTrackData = (tabId: number) => {
    trackGdp("P11222", "B031", {
      tab_name: memberCardTabItems.find((item) => item.key === tabId)?.title,
      ...getReportVipData(levelInfo),
      ...getReportCompData(comp),
    });
  };

  useEffect(() => {
    if (moduleJsonData?.goodsInfo?.buyAgreement) {
      setBuyAgreement(moduleJsonData?.goodsInfo?.buyAgreement);
    }
    if (moduleJsonData?.goodsInfo?.detailRule) {
      setDetailRule(moduleJsonData?.goodsInfo?.detailRule);
    }
  }, [
    moduleJsonData?.goodsInfo?.buyAgreement,
    moduleJsonData?.goodsInfo?.detailRule,
    setBuyAgreement,
    setDetailRule,
  ]);

  useEffect(() => {
    if (group?.length && goodsData) {
      // 从全局商品数据中匹配当前组件需要的商品数据
      const newGroups = group.map((group) => {
        const goodsList = group.goodsList.map((goods) => {
          const goodsDetail = goodsData[goods.skuId];
          return {
            ...goods,
            ...goodsDetail,
          };
        });
        return {
          ...group,
          goodsList,
        };
      });
      setCurrentGroups(newGroups);
    }
  }, [group, goodsData, setCurrentGroups]);

  useEffect(() => {
    console.log("currentGroups", currentGroups);
    if (!currentGroups?.length || !memberCardTabItems?.length) {
      return;
    }
    selectFocusGoods();
  }, [currentGroups, memberCardTabItems]);

  const selectFocusGoods = () => {
    if (spuId && marketingGoodItem && marketingGoodItem?.spuId === spuId) {
      const targetGroup = currentGroups.find((item) =>
        item.goodsList.some((goods) => goods.spuId === spuId),
      );
      HomeTabsBarRef.current?.setHlTab(targetGroup?.id || null);
      setMemberBuyCurrentTab(targetGroup?.id || null);
      setFocusedGoods(marketingGoodItem);
      setCurrentGoodsList(targetGroup?.goodsList || []);
    } else {
      HomeTabsBarRef.current?.clickTab(memberCardTabItems[0]);
      setFocusedGoods(currentGroups[0]?.goodsList[0] || null);
      setCurrentGoodsList(
        currentGroups.find((item) => item.id === memberCardTabItems[0]?.key)
          ?.goodsList || [],
      );
    }
  };

  const clickTabItem = (tabId: number) => {
    console.log("clickTabItem", tabId);
    const goodsList =
      currentGroups.find((item) => item.id === tabId)?.goodsList || [];
    setCurrentGoodsList(goodsList);
    setFocusedGoods(goodsList[0] || null);
    setActiveKey(tabId);
  };

  // 新增：监听containerRef的可见性,可见时执行selectFocusGoods，用于打开弹窗时刷新当前商品列表
  useEffect(() => {
    if (!isInPopup) {
      return;
    }
    const element = containerRef.current;
    if (!element) {
      console.warn(
        "buyContentRef.current is null, IntersectionObserver not created",
      );
      return;
    }

    // 新增：监听containerRef的可见性,可见时执行selectFocusGoods，用IntersectionObserver实现
    const observer = new IntersectionObserver(
      ([entry]) => {
        console.log("IntersectionObserver visible", entry.isIntersecting);
        if (entry.isIntersecting) {
          selectFocusGoods();
        }
      },
      {
        threshold: 0.1, // 多个阈值，提高检测精度
      },
    );

    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
      observer.disconnect();
      console.log("IntersectionObserver cleaned up");
    };
  }, [containerRef.current]);

  // 新增：监听原BuyGoodContent的可见性<div className="good-buy-content" ref={buyContentRef}><div className="good-buy-content" ref={buyContentRef}>
  useEffect(() => {
    const element = buyContentRef.current;
    if (!element) {
      console.warn(
        "buyContentRef.current is null, IntersectionObserver not created",
      );
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsOriginalBuyContentVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1, // 多个阈值，提高检测精度
        rootMargin: "-100px", // 移除负值margin
      },
    );

    observer.observe(element);
    console.log("IntersectionObserver created and observing element:", element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
      observer.disconnect();
      console.log("IntersectionObserver cleaned up");
    };
  }, [buyContentRef.current]);

  useEffect(() => {
    if (!currentGroups?.length || !comp || !levelInfo) {
      return;
    }
    const activityld = currentGroups
      ?.map((item) => item?.goodsList)
      ?.flatMap((item) => item)
      ?.map((item) => item?.actCode)
      ?.filter(Boolean)
      ?.join(",");
    trackGdp("P11222", "B004", {
      activityld,
      versionId: configPageQuery?.data?.versionCode,
      use_coupon: currentGroups?.some((item) =>
        item?.goodsList?.some(
          (goods) => goods?.discountType === "COUPON_PRICE",
        ),
      )
        ? "是"
        : "否",
      ...getReportCompData(comp),
      ...getReportVipData(levelInfo),
    });
  }, [currentGroups, comp, levelInfo]);

  const [measureRef, { height }] = useMeasure();
  const styles = useSpring({
    height,
    config: { ...config.stiff, tension: 250, friction: 30 },
  });

  if (!currentGroups?.length) {
    return <></>;
  }

  // 构建组件内容
  const renderContent = () => {
    // 一行只有一个带购买按钮商品
    if (type === "ONE_ROW_GOODS_BUY") {
      // 在弹窗内部不显示点击区域和弹窗功能，避免递归
      if (isInPopup) {
        return (
          <div className="good-single-row one-row">
            <div className="good-item-content fill-row-item">
              <GeneralGoodsItem
                goodItem={
                  marketingGoodItem || currentGroups?.[0].goodsList?.[0]
                }
                index={0}
                type={"SINGLE_ROW_GOODS_BUY"}
                doBuyMemberGoods={doBuyMemberGoods}
              />
            </div>
          </div>
        );
      }

      // 正常模式下显示点击区域和弹窗
      return (
        <>
          <div className="good-single-row one-row">
            <div
              className="good-item-content fill-row-item"
              onClick={() => GoodsPopupRef.current?.onOpen()}
            >
              <GeneralGoodsItem
                goodItem={
                  marketingGoodItem || currentGroups?.[0].goodsList?.[0]
                }
                index={0}
                type={"SINGLE_ROW_GOODS_BUY"}
              />
            </div>
          </div>

          {/* 底部弹窗，显示商品详情列表 */}

          <PopupCom ref={GoodsPopupRef} title="选择商品" height={"auto"}>
            <div
              className="popup-tab-goods-content"
              onTouchMove={(e) => {
                e.stopPropagation();
              }}
            >
              <TabGoodsContent
                comp={comp}
                moduleJsonData={moduleJsonData}
                group={group}
                type="ONE_ROW_X_GOODS"
                isInPopup={true}
              />
            </div>
          </PopupCom>
        </>
      );
    }
    // 一行一个多行带购买按钮商品
    else if (type === "ONE_ROW_ONE_GOODS_BUY") {
      return (
        <>
          <HomeTabsBar
            ref={HomeTabsBarRef}
            showIcon={true}
            firstShowTabKey={memberBuyCurrentTab}
            tabItems={memberCardTabItems}
            showSubTitle={memberCardTabItems.length <= 3}
            changeActiveTab={(tab) => {
              clickTabItem(tab);
              tabsTrackData(tab);
            }}
          />
          <animated.div style={{ overflow: "hidden", ...styles }}>
            <div ref={measureRef}>
              {currentGoodsList.map((listItem, index) => {
                return (
                  <div
                    key={index}
                    className="good-item-content fill-row-item good-single-row"
                  >
                    <GeneralGoodsItem
                      goodItem={listItem}
                      index={index}
                      type={"FILL_ROW_GOODS_BUY"}
                      doBuyMemberGoods={doBuyMemberGoods}
                    />
                  </div>
                );
              })}
              <AgreementRow />
            </div>
          </animated.div>
        </>
      );
    }

    // 一排X格默认设置白色背景
    return (
      <div
        style={{
          backgroundColor: type === "ONE_ROW_X_GOODS" ? "#FFF" : "",
        }}
      >
        <HomeTabsBar
          ref={HomeTabsBarRef}
          showIcon={true}
          firstShowTabKey={memberBuyCurrentTab}
          tabItems={memberCardTabItems}
          showSubTitle={memberCardTabItems.length <= 3}
          changeActiveTab={(tab) => {
            clickTabItem(tab);
            tabsTrackData(tab);
          }}
        />
        <animated.div style={{ overflow: "hidden", ...styles }}>
          <div ref={measureRef}>
            {type === "ONE_ROW_X_GOODS_BUY" && (
              <div className="good-item-content fill-row-item good-single-row">
                <GeneralGoodsItem
                  goodItem={currentGoodsList[0]}
                  index={0}
                  type={"FILL_ROW_GOODS_BUY"}
                  doBuyMemberGoods={doBuyMemberGoods}
                />
              </div>
            )}
            {/* 第二排X格 */}
            <div className={`good-item-list-wrap`} key={activeKey}>
              <div
                className={`good-item-list`}
                style={{
                  flexDirection: "row",
                }}
                onScroll={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {currentGoodsList.map((listItem, index) => {
                  if (type === "ONE_ROW_X_GOODS_BUY") {
                    if (index === 0) {
                      // 跳过第一个
                      return null;
                    }
                    return (
                      <div
                        key={index}
                        className={`good-item-content small-row-item half`}
                      >
                        <GeneralGoodsItem
                          key={index}
                          goodItem={listItem}
                          index={index}
                          type={"SMALL_ROW_GOODS_BUY"}
                          doBuyMemberGoods={doBuyMemberGoods}
                        />
                      </div>
                    );
                  } else if (type === "ONE_ROW_X_GOODS") {
                    return (
                      <div
                        key={index}
                        className={`good-item-content small-row-item ${currentGoodsList.length <= 2 ? "half" : ""}`}
                      >
                        <GeneralGoodsItem
                          key={index}
                          goodItem={listItem}
                          index={index}
                          type={"SMALL_GOODS"}
                          doBuyMemberGoods={doBuyMemberGoods}
                        />
                      </div>
                    );
                  } else {
                    return <></>;
                  }
                })}
              </div>
            </div>
            {/* 商品介绍图和购买内容 */}
            <div className="good-buy-content">
              {focusedGoods?.customPicture && type === "ONE_ROW_X_GOODS" && (
                <Image
                  placeholder={null}
                  className="desc-img"
                  src={focusedGoods?.customPicture || ""}
                  alt=""
                />
              )}
              {type && ["ONE_ROW_X_GOODS_BUY"].includes(type) ? (
                <AgreementRow />
              ) : (
                <div ref={buyContentRef}>
                  <BuyGoodContent doBuyMemberGoods={doBuyMemberGoods} />
                </div>
              )}
            </div>
          </div>
        </animated.div>
      </div>
    );
  };

  // 如果有背景图或间距，包装在容器中
  return (
    <div
      className={bgUrl ? "visual-style-container" : undefined}
      style={{
        paddingTop: spacing ? `${spacing}px` : undefined, // 内部元素距离背景图上端的距离
        backgroundImage: bgUrl ? `url(${processOssImage(bgUrl)})` : undefined,
      }}
    >
      <div ref={containerRef}>{renderContent()}</div>
      {/* 吸底的BuyGoodContent - 仅当是第一个TabGoodsContent且原组件不可见时显示 */}
      {isShowButtomBuyContent && !isOriginalBuyContentVisible && (
        <div className="sticky-buy-good-content">
          <BuyGoodContent doBuyMemberGoods={doBuyMemberGoods} />
        </div>
      )}
    </div>
  );
};

export default TabGoodsContent;
